import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { hasValidAssignmentPolygon } from "../api/assignmentArea";
import { getAssignments, useDeleteAssignment } from "../api/assignments";
import {
    exportAssignmentsAsA4Images,
    exportAssignmentsAsA4Pdf,
    exportAssignmentAsA4Image
} from "./exportAssignmentImages";
import { AssignmentForm } from "./AssignmentForm";
import { useMunicipalities } from "../api/map";
import { useKretsMembers } from "../api/kretsMembers";
import { useMapContextOptional } from "../map/MapContext";
import { useSelectedMunicipalityId } from "../admin/selectedMunicipality";
import "./assignments.css";

interface AssignmentsPanelProps {
    onMunicipalityChange?: (id: string, code: string) => void;
    /** Zooms map back to the whole selected municipality (clears any highlighted assignment area). */
    onShowWholeMunicipality?: () => void;
}

export const AssignmentsPanel = ({ onMunicipalityChange, onShowWholeMunicipality }: AssignmentsPanelProps = {}): JSX.Element => {
    const { t } = useTranslation();
    const mapContext = useMapContextOptional();
    const [isCreating, setIsCreating] = useState(false);
    // ?edit=<id> öppnar formuläret direkt — så fungerar "Redigera" från tabellvyn.
    const [editingId, setEditingId] = useState<string | null>(
        () => new URLSearchParams(window.location.search).get("edit")
    );
    const urlEditIdRef = useRef(new URLSearchParams(window.location.search).get("edit"));
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useSelectedMunicipalityId();
    const [isExporting, setIsExporting] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [isExportingSingle, setIsExportingSingle] = useState(false);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["assignments", selectedMunicipalityId],
        queryFn: () => getAssignments(undefined, undefined, selectedMunicipalityId),
        enabled: !!selectedMunicipalityId,
        staleTime: 60_000,
        retry: 2
    });

    const municipalitiesQuery = useMunicipalities(
        { limit: 2000, includeGeometry: false },
        null,
        { enabled: true }
    );

    const municipalities = useMemo(
        () =>
            (municipalitiesQuery.data?.features ?? [])
                .map((feature) => ({
                    id: String(feature.id ?? feature.properties.municipalityPolygonId),
                    name: feature.properties.name,
                    code: feature.properties.code
                }))
                .sort((a, b) => a.name.localeCompare(b.name, "sv")),
        [municipalitiesQuery.data?.features]
    );

    // Utdelarens namn löses upp som i utdelningslistan: kretsmedlemmen först, sedan uppdragets
    // egna fält (äldre uppdrag kan sakna kretsMemberId).
    const { data: kretsMembers = [] } = useKretsMembers();
    const kretsMemberById = useMemo(
        () => new Map(kretsMembers.map((member) => [member.id, member])),
        [kretsMembers]
    );

    const sortedAssignments = useMemo(
        () =>
            [...(data ?? [])].sort((a, b) =>
                a.name.localeCompare(b.name, "sv", { numeric: true, sensitivity: "base" })
            ),
        [data]
    );

    const getDistributorName = (assignment: (typeof sortedAssignments)[number]): string | undefined =>
        (assignment.kretsMemberId ? kretsMemberById.get(assignment.kretsMemberId)?.name : undefined) ??
        assignment.kretsMemberName ??
        assignment.distributorName;

    const totalTargetAddresses = useMemo(
        () => (data ?? []).reduce((sum, assignment) => sum + (assignment.targetAddressCount ?? 0), 0),
        [data]
    );

    // Kommunen kan komma från delat state (satt på en annan admin-sida), så kartan måste få
    // id + kod när listan laddats — inte bara när användaren byter i väljaren.
    useEffect(() => {
        const code = municipalities.find((m) => m.id === selectedMunicipalityId)?.code ?? "";
        onMunicipalityChange?.(selectedMunicipalityId, code);
    }, [selectedMunicipalityId, municipalities, onMunicipalityChange]);

    const deleteMutation = useDeleteAssignment();

    const handleDelete = async (id: string, onDeleted?: () => void) => {
        if (window.confirm(t("assignments.actions.confirmDelete"))) {
            deleteMutation.mutate(id, {
                onSuccess: () => {
                    onDeleted?.();
                    refetch();
                }
            });
        }
    };

    const handleCreateSuccess = () => {
        mapContext?.setPolygonArea(null);
        setIsCreating(false);
        refetch();
    };

    /** Stänger formuläret och plockar bort ?edit= så en omladdning inte öppnar det igen. */
    const closeEditor = () => {
        setEditingId(null);
        urlEditIdRef.current = null;
        const params = new URLSearchParams(window.location.search);
        if (params.has("edit")) {
            params.delete("edit");
            const query = params.toString();
            window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
        }
    };

    const handleEditSuccess = () => {
        mapContext?.setPolygonArea(null);
        closeEditor();
        refetch();
    };

    const editingAssignment = editingId ? data?.find((a) => a.id === editingId) : undefined;

    // Öppnat via länk: visa uppdragets yta på kartan så fort listan hunnit laddas.
    useEffect(() => {
        if (!urlEditIdRef.current || !editingAssignment || editingAssignment.id !== urlEditIdRef.current) {
            return;
        }
        urlEditIdRef.current = null;
        mapContext?.setHighlightedAssignmentArea(editingAssignment.area ?? null, {
            assignmentId: editingAssignment.id
        });
    }, [editingAssignment, mapContext]);

    const exportDisabled =
        isExporting || isExportingPdf || isLoading || !selectedMunicipalityId || !data || data.length === 0;

    const handleExportA4Images = async () => {
        if (!data || data.length === 0) {
            return;
        }

        setIsExporting(true);
        try {
            const result = await exportAssignmentsAsA4Images(sortedAssignments, ({ current, total, name }) => {
                toast.loading(`Exporterar ${current}/${total}: ${name}`, { id: "assignments-export" });
            });
            toast.success(
                `Klart: ${result.exported} bilder exporterade${result.skipped > 0 ? `, ${result.skipped} hoppades över` : ""}.`,
                { id: "assignments-export" }
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : "Export misslyckades.";
            toast.error(message, { id: "assignments-export" });
        } finally {
            setIsExporting(false);
        }
    };

    /** Samma bilder, men samlade i en PDF med en liggande A4-sida per uppdrag – klar att skriva ut. */
    const handleExportA4Pdf = async () => {
        if (!data || data.length === 0) {
            return;
        }

        setIsExportingPdf(true);
        try {
            const result = await exportAssignmentsAsA4Pdf(sortedAssignments, ({ current, total, name }) => {
                toast.loading(`Skapar PDF ${current}/${total}: ${name}`, { id: "assignments-export-pdf" });
            });
            toast.success(
                `Klart: PDF med ${result.exported} sidor${result.skipped > 0 ? `, ${result.skipped} hoppades över` : ""}.`,
                { id: "assignments-export-pdf" }
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : "Export misslyckades.";
            toast.error(message, { id: "assignments-export-pdf" });
        } finally {
            setIsExportingPdf(false);
        }
    };

    // Exports the saved assignment — changes in the form must be saved first to show up in the image.
    const handleExportSingleA4Image = async () => {
        if (!editingAssignment) {
            return;
        }

        setIsExportingSingle(true);
        toast.loading(`Exporterar ${editingAssignment.name}...`, { id: "assignment-export-single" });
        try {
            await exportAssignmentAsA4Image(editingAssignment);
            toast.success("Bilden har laddats ner.", { id: "assignment-export-single" });
        } catch (error) {
            const message = error instanceof Error ? error.message : "Export misslyckades.";
            toast.error(message, { id: "assignment-export-single" });
        } finally {
            setIsExportingSingle(false);
        }
    };

    return (
        <section className="app-panel" aria-labelledby="assignments-panel__title">
            <header className="app-panel__header">
                <h2 id="assignments-panel__title" className="app-panel__title">
                    {t("assignments.title")}
                </h2>
                <p className="app-panel__description">{t("assignments.description")}</p>
            </header>

            {isCreating && (
                <div className="assignments-form-container">
                    <AssignmentForm
                        defaultMunicipalityPolygonId={selectedMunicipalityId}
                        onSuccess={handleCreateSuccess}
                        onCancel={() => {
                            mapContext?.setPolygonArea(null);
                            setIsCreating(false);
                        }}
                    />
                </div>
            )}

            {editingId && editingAssignment && (
                <div className="assignments-form-container">
                    <AssignmentForm
                        assignment={editingAssignment}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            mapContext?.setPolygonArea(null);
                            closeEditor();
                        }}
                        onDelete={() =>
                            handleDelete(editingAssignment.id, () => {
                                mapContext?.setPolygonArea(null);
                                mapContext?.setHighlightedAssignmentArea(null);
                                closeEditor();
                            })
                        }
                        isDeleting={deleteMutation.isPending}
                        onExportA4Image={handleExportSingleA4Image}
                        isExportingA4Image={isExportingSingle}
                    />
                </div>
            )}

            {!isCreating && !editingId && (
                <div className="assignments-list">
                    <label className="assignments-panel__municipality-filter">
                        {t("assignments.fields.municipality", "Kommun")}
                        <select
                            value={selectedMunicipalityId}
                            onChange={(event) => {
                                mapContext?.setHighlightedAssignmentArea(null);
                                setSelectedMunicipalityId(event.target.value);
                            }}
                        >
                            <option value="">{t("assignments.placeholders.municipality", "Välj kommun")}</option>
                            {municipalities.map((municipality) => (
                                <option key={municipality.id} value={municipality.id}>
                                    {municipality.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    {!selectedMunicipalityId && (
                        <p className="assignments-list__hint">
                            {t("assignments.states.selectMunicipalityFirst", "Välj kommun för att visa uppdrag.")}
                        </p>
                    )}

                    {selectedMunicipalityId && !isCreating && !editingId && (
                        <div className="assignments-panel__actions">
                            <button
                                type="button"
                                className="assignments-panel__create-button"
                                onClick={() => {
                                    mapContext?.setHighlightedAssignmentArea(null);
                                    setIsCreating(true);
                                }}
                            >
                                {t("assignments.actions.create")}
                            </button>
                            <button
                                type="button"
                                className="assignments-panel__export-button"
                                disabled={exportDisabled}
                                onClick={handleExportA4Images}
                                title="Exportera en A4 liggande PNG per uppdrag i vald kommun"
                            >
                                {isExporting ? "Exporterar..." : "Exportera A4-bilder"}
                            </button>
                            <button
                                type="button"
                                className="assignments-panel__export-button"
                                disabled={exportDisabled}
                                onClick={handleExportA4Pdf}
                                title="En PDF med en liggande A4-sida per uppdrag i vald kommun – klar att skriva ut"
                            >
                                {isExportingPdf ? "Skapar PDF..." : "Exportera PDF"}
                            </button>
                            <Link
                                to="/admin/assignments/table"
                                className="assignments-panel__table-link"
                                title="Utskriftsvänlig lista med adress, telefon och materialkvittering"
                            >
                                Tabellvy
                            </Link>
                        </div>
                    )}

                    {isLoading && <p>{t("assignments.states.loading")}</p>}
                    {isError && (
                        <p role="alert" className="assignments-list__error">
                            {t("assignments.states.error")}
                            {" "}
                            <button type="button" className="assignments-list__retry-button" onClick={() => refetch()}>
                                Försök igen
                            </button>
                        </p>
                    )}
                    {!isLoading && !isError && selectedMunicipalityId && data?.length === 0 && (
                        <p>{t("assignments.states.empty")}</p>
                    )}

                    {sortedAssignments.map((assignment) => (
                        <article key={assignment.id} className="assignments-card">
                            <header className="assignments-card__header">
                                <div className="assignments-card__identity">
                                    <h3>{assignment.name}</h3>
                                    {getDistributorName(assignment) && (
                                        <p className="assignments-card__distributor">{getDistributorName(assignment)}</p>
                                    )}
                                </div>
                                {assignment.targetAddressCount !== undefined && (
                                    <span className="assignments-card__count">{assignment.targetAddressCount}</span>
                                )}
                                <div className="assignments-card__actions">
                                    {assignment.status === "planned" &&
                                        mapContext &&
                                        hasValidAssignmentPolygon(assignment.area) && (
                                            <button
                                                type="button"
                                                className="assignments-card__show-area-button"
                                                onClick={() =>
                                                    mapContext.setHighlightedAssignmentArea(assignment.area!, {
                                                        assignmentId: assignment.id
                                                    })
                                                }
                                            >
                                                {t("assignments.actions.showAreaOnMapShort", "Visa")}
                                            </button>
                                        )}
                                    <button
                                        type="button"
                                        className="assignments-card__edit-button"
                                        onClick={() => {
                                            // Show existing area on map while editing (cleared when drawing starts)
                                            mapContext?.setHighlightedAssignmentArea(assignment.area ?? null, {
                                                assignmentId: assignment.id
                                            });
                                            setEditingId(assignment.id);
                                        }}
                                    >
                                        {t("assignments.actions.edit")}
                                    </button>
                                </div>
                            </header>
                        </article>
                    ))}

                    {selectedMunicipalityId && !isLoading && !isError && (
                        <div className="assignments-list__footer">
                            <p className="assignments-list__sum">
                                {t("assignments.fields.totalAddresses", "Totalt antal adresser")}: {totalTargetAddresses}
                            </p>
                            {onShowWholeMunicipality && (
                                <button
                                    type="button"
                                    className="assignments-list__show-municipality-button"
                                    onClick={onShowWholeMunicipality}
                                    title="Zooma ut kartan till hela vald kommun"
                                >
                                    {t("assignments.actions.showWholeMunicipality", "Visa hela kommunen")}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default AssignmentsPanel;